<template>
  <v-card
    class="module-card"
    :class="{ 'module-card--disabled': disabled }"
    :hover="!disabled"
    elevation="4"
    height="400"
    width="360"
    @click="handleClick"
  >
    <v-card-text class="d-flex flex-column align-center fill-height pa-0">
      <div class="card-title-bar">
        <!-- v-html is used so titles like "Med O.S.® for<br>Infusion Safety" render with an explicit line break -->
        <h2
          class="text-h4 text-center full-width card-title"
          v-html="title"
        ></h2>
      </div>
      <div class="card-content">
        <h3
          v-if="!disabled"
          class="text-subtitle-1 mb-6 text-center full-width card-subheader"
        >
          {{ subheader }}
        </h3>
        <h3
          v-else
          class="text-subtitle-1 mb-6 text-center full-width card-subheader"
        >
          Contact us for access
        </h3>
        <div class="card-hover-area">
          <div class="card-icon-wrapper mb-4">
            <div v-if="disabled">
              <v-icon
                icon="mdi-lock"
                color="white"
                size="100"
              />
            </div>
            <div v-else>
              <v-icon
                v-if="isMdiIcon"
                :icon="iconSrc"
                color="white"
                size="100"
              />
              <img
                v-else
                :src="iconSrc"
                :alt="iconAlt"
                class="card-icon"
              />
            </div>
          </div>
          <p class="text-body-1 text-left text-medium-emphasis full-width card-description">
            {{ description }}
          </p>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'ModuleCard',
  props: {
    title: {
      type: String,
      required: true,
    },
    subheader: {
      type: String,
      required: true,
    },
    iconSrc: {
      type: String,
      required: true,
    },
    iconAlt: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      required: true,
    },
    moduleRoute: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  computed: {
    isMdiIcon() {
      return typeof this.iconSrc === 'string' && this.iconSrc.startsWith('mdi-');
    },
  },
  methods: {
    handleClick() {
      if (this.disabled) return;
      this.navigateTo(); // or $emit('click'), whichever version you landed on
    },
    navigateTo() {
      this.$router.push({ path: this.moduleRoute });
    },
  },
};
</script>

<style scoped>
.module-card {
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}

.module-card:hover:not(.v-card--disabled) {
  transform: translateY(-4px);
}

.module-card--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.module-card--disabled:hover {
  transform: none;
}

.module-card--disabled .card-title-bar,
.module-card--disabled .card-icon-wrapper {
  background-color: #9e9e9e;
}

.full-width {
  width: 100%;
}

.card-title-bar {
  background-color: #255483;
  width: 100%;
  padding: 24px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-title {
  font-weight: bold;
  color: white !important;
  /* Sized so a three-line title ("Med O.S.® for / IV Formulation Management",
     which wraps at this card width) still leaves the icon room to breathe
     inside the card's fixed 400px height. At the previous 1.75rem/1.175 the
     bar ran ~147px tall and pushed the icon block against the bottom edge. */
  font-size: 1.5rem !important;
  line-height: 1.15;
  margin: 0 !important;
}

.card-content {
  padding: 24px;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.card-subheader {
  color: #888;
  font-weight: bold;
  font-size: 1.1rem !important;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-icon-wrapper {
  background-color: #255483;
  padding: 16px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 132px;
  height: 132px;
}

.card-icon {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.card-hover-area {
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.card-description {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  background-color: white;
  padding: 16px;
  border-radius: 8px;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.module-card:hover .card-description {
  opacity: 1;
}
</style>
